/**
 * Notification Preferences Page
 *
 * Route: /settings/notifications | Nav: Settings > Notifications | Roles: TenantAdmin/Admin
 *
 * Manage notification preferences, channels, and view delivery log.
 */
import React, { useState, useEffect } from 'react';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  tokens,
  Spinner,
  Button,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableColumnDefinition,
  createTableColumn,
  Switch,
  Tab,
  TabList,
} from '@fluentui/react-components';
import {
  SaveRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationSettingsApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import type { NotificationPreference, NotificationChannel, NotificationDelivery } from '../types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const getDeliveryStatusColor = (
  status: NotificationDelivery['status']
): 'success' | 'danger' | 'warning' => {
  switch (status) {
    case 'Delivered':
      return 'success';
    case 'Failed':
      return 'danger';
    case 'Pending':
      return 'warning';
    default:
      return 'warning';
  }
};

// ---------------------------------------------------------------------------
// NotificationPreferencesPage
// ---------------------------------------------------------------------------

export const NotificationPreferencesPage: React.FC = () => {
  const ps = usePageStyles();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState<'preferences' | 'channels' | 'deliveries'>('preferences');
  const [editedPreferences, setEditedPreferences] = useState<NotificationPreference[]>([]);

  // Queries
  const { data: preferences, isLoading: prefsLoading } = useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: () => notificationSettingsApi.getPreferences(),
  });

  const { data: channels, isLoading: channelsLoading } = useQuery({
    queryKey: ['notificationChannels'],
    queryFn: () => notificationSettingsApi.getChannels(),
    enabled: selectedTab === 'channels',
  });

  const { data: deliveries, isLoading: deliveriesLoading } = useQuery({
    queryKey: ['notificationDeliveries'],
    queryFn: () => notificationSettingsApi.getDeliveries(),
    enabled: selectedTab === 'deliveries',
  });

  // Sync edited preferences when data loads
  useEffect(() => {
    if (preferences) {
      setEditedPreferences(preferences.map((p) => ({ ...p })));
    }
  }, [preferences]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (prefs: NotificationPreference[]) =>
      notificationSettingsApi.updatePreferences(prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationPreferences'] });
    },
  });

  // Toggle handler
  const handleToggle = (
    eventType: string,
    channel: 'emailEnabled' | 'slackEnabled' | 'teamsEnabled',
    checked: boolean
  ) => {
    setEditedPreferences((prev) =>
      prev.map((p) =>
        p.eventType === eventType ? { ...p, [channel]: checked } : p
      )
    );
  };

  // Preference columns
  const preferenceColumns: TableColumnDefinition<NotificationPreference>[] = [
    createTableColumn<NotificationPreference>({
      columnId: 'eventType',
      compare: (a, b) => a.eventLabel.localeCompare(b.eventLabel),
      renderHeaderCell: () => 'Event Type',
      renderCell: (item) => <Body1 style={{ fontWeight: 500 }}>{item.eventLabel}</Body1>,
    }),
    createTableColumn<NotificationPreference>({
      columnId: 'email',
      renderHeaderCell: () => 'Email',
      renderCell: (item) => {
        const edited = editedPreferences.find((p) => p.eventType === item.eventType);
        return (
          <Switch
            checked={edited?.emailEnabled ?? item.emailEnabled}
            onChange={(_, data) => handleToggle(item.eventType, 'emailEnabled', data.checked)}
          />
        );
      },
    }),
    createTableColumn<NotificationPreference>({
      columnId: 'slack',
      renderHeaderCell: () => 'Slack',
      renderCell: (item) => {
        const edited = editedPreferences.find((p) => p.eventType === item.eventType);
        return (
          <Switch
            checked={edited?.slackEnabled ?? item.slackEnabled}
            onChange={(_, data) => handleToggle(item.eventType, 'slackEnabled', data.checked)}
          />
        );
      },
    }),
    createTableColumn<NotificationPreference>({
      columnId: 'teams',
      renderHeaderCell: () => 'Teams',
      renderCell: (item) => {
        const edited = editedPreferences.find((p) => p.eventType === item.eventType);
        return (
          <Switch
            checked={edited?.teamsEnabled ?? item.teamsEnabled}
            onChange={(_, data) => handleToggle(item.eventType, 'teamsEnabled', data.checked)}
          />
        );
      },
    }),
  ];

  // Channel columns
  const channelColumns: TableColumnDefinition<NotificationChannel>[] = [
    createTableColumn<NotificationChannel>({
      columnId: 'type',
      compare: (a, b) => a.type.localeCompare(b.type),
      renderHeaderCell: () => 'Type',
      renderCell: (item) => <Badge appearance="outline">{item.type}</Badge>,
    }),
    createTableColumn<NotificationChannel>({
      columnId: 'target',
      compare: (a, b) => a.target.localeCompare(b.target),
      renderHeaderCell: () => 'Target',
      renderCell: (item) => <Body1>{item.target}</Body1>,
    }),
    createTableColumn<NotificationChannel>({
      columnId: 'verified',
      renderHeaderCell: () => 'Verified',
      renderCell: (item) =>
        item.isVerified ? (
          <Badge appearance="filled" color="success">Verified</Badge>
        ) : (
          <Badge appearance="filled" color="warning">Unverified</Badge>
        ),
    }),
    createTableColumn<NotificationChannel>({
      columnId: 'created',
      compare: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      renderHeaderCell: () => 'Created',
      renderCell: (item) => <Body2>{formatDate(item.createdAt)}</Body2>,
    }),
  ];

  // Delivery columns
  const deliveryColumns: TableColumnDefinition<NotificationDelivery>[] = [
    createTableColumn<NotificationDelivery>({
      columnId: 'channel',
      compare: (a, b) => a.channelType.localeCompare(b.channelType),
      renderHeaderCell: () => 'Channel',
      renderCell: (item) => <Badge appearance="outline">{item.channelType}</Badge>,
    }),
    createTableColumn<NotificationDelivery>({
      columnId: 'event',
      compare: (a, b) => a.eventType.localeCompare(b.eventType),
      renderHeaderCell: () => 'Event',
      renderCell: (item) => <Body1>{item.eventType}</Body1>,
    }),
    createTableColumn<NotificationDelivery>({
      columnId: 'status',
      renderHeaderCell: () => 'Status',
      renderCell: (item) => (
        <Badge appearance="filled" color={getDeliveryStatusColor(item.status)}>
          {item.status}
        </Badge>
      ),
    }),
    createTableColumn<NotificationDelivery>({
      columnId: 'sentAt',
      compare: (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
      renderHeaderCell: () => 'Sent',
      renderCell: (item) => <Body2>{formatDate(item.sentAt)}</Body2>,
    }),
    createTableColumn<NotificationDelivery>({
      columnId: 'error',
      renderHeaderCell: () => 'Error',
      renderCell: (item) => (
        <Body2 style={{ color: tokens.colorPaletteRedForeground1 }}>
          {item.errorMessage || '-'}
        </Body2>
      ),
    }),
  ];

  // Loading state
  if (prefsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading notification preferences..." />
      </div>
    );
  }

  return (
    <div className={ps.page}>
      <Title1>Notification Preferences</Title1>

      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) =>
          setSelectedTab(data.value as typeof selectedTab)
        }
      >
        <Tab value="preferences">Preferences</Tab>
        <Tab value="channels">Channels</Tab>
        <Tab value="deliveries">Delivery Log</Tab>
      </TabList>

      {/* Preferences Tab */}
      {selectedTab === 'preferences' && (
        <Card>
          <CardHeader
            header={<Title2>Event Notification Settings</Title2>}
            description="Toggle notification channels for each event type"
            action={
              <Button
                appearance="primary"
                icon={<SaveRegular />}
                onClick={() => saveMutation.mutate(editedPreferences)}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            }
          />
          {editedPreferences.length > 0 ? (
            <DataGrid
              items={editedPreferences}
              columns={preferenceColumns}
              sortable
              getRowId={(item) => item.eventType}
              style={{ minWidth: '600px' }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<NotificationPreference>>
                {({ item, rowId }) => (
                  <DataGridRow<NotificationPreference> key={rowId}>
                    {({ renderCell }) => (
                      <DataGridCell>{renderCell(item)}</DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          ) : (
            <Body1 className={ps.emptyState}>
              No notification preferences configured.
            </Body1>
          )}
        </Card>
      )}

      {/* Channels Tab */}
      {selectedTab === 'channels' && (
        <Card>
          <CardHeader
            header={<Title2>Notification Channels</Title2>}
            description="Configured notification delivery channels"
            action={
              <Button appearance="primary" icon={<AddRegular />} disabled>
                Add Channel
              </Button>
            }
          />
          {channelsLoading ? (
            <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
              <Spinner label="Loading channels..." />
            </div>
          ) : channels && channels.length > 0 ? (
            <DataGrid
              items={channels}
              columns={channelColumns}
              sortable
              getRowId={(item) => item.id}
              style={{ minWidth: '600px' }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<NotificationChannel>>
                {({ item, rowId }) => (
                  <DataGridRow<NotificationChannel> key={rowId}>
                    {({ renderCell }) => (
                      <DataGridCell>{renderCell(item)}</DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          ) : (
            <Body1 className={ps.emptyState}>
              No notification channels configured yet.
            </Body1>
          )}
        </Card>
      )}

      {/* Delivery Log Tab */}
      {selectedTab === 'deliveries' && (
        <Card>
          <CardHeader
            header={<Title2>Delivery Log</Title2>}
            description="Recent notification delivery attempts"
          />
          {deliveriesLoading ? (
            <div style={{ padding: tokens.spacingVerticalXXL, textAlign: 'center' }}>
              <Spinner label="Loading delivery log..." />
            </div>
          ) : deliveries && deliveries.length > 0 ? (
            <DataGrid
              items={deliveries}
              columns={deliveryColumns}
              sortable
              getRowId={(item) => item.id}
              style={{ minWidth: '700px' }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell }) => (
                    <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<NotificationDelivery>>
                {({ item, rowId }) => (
                  <DataGridRow<NotificationDelivery> key={rowId}>
                    {({ renderCell }) => (
                      <DataGridCell>{renderCell(item)}</DataGridCell>
                    )}
                  </DataGridRow>
                )}
              </DataGridBody>
            </DataGrid>
          ) : (
            <Body1 className={ps.emptyState}>
              No notification deliveries recorded yet.
            </Body1>
          )}
        </Card>
      )}
    </div>
  );
};
